import { createContext, useContext, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Ctx = { value: string; onChange: (v: string) => void };
const TabsCtx = createContext<Ctx | null>(null);

export function Tabs({
  value,
  onValueChange,
  children,
  className,
}: {
  value: string;
  onValueChange: (v: string) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <TabsCtx.Provider value={{ value, onChange: onValueChange }}>
      <div className={className}>{children}</div>
    </TabsCtx.Provider>
  );
}

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      role="tablist"
      // rc116 — flex-wrap + min-h en lugar de h fija: con 5 pestañas el strip
      // no cabe en un iPhone y las últimas quedaban cortadas (el modal ya no
      // panea horizontal desde rc115, así que simplemente se perdían). Ahora
      // envuelven a una segunda fila cuando no caben; en desktop se ve igual.
      className={cn(
        "inline-flex min-h-8 max-w-full flex-wrap items-center justify-start gap-1 rounded-md bg-muted p-0.5 text-muted-foreground",
        className
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  disabled,
}: {
  value: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("TabsTrigger needs Tabs parent");
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={() => ctx.onChange(value)}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-xs font-medium transition-all",
        "disabled:pointer-events-none disabled:opacity-50",
        active
          ? "bg-background text-foreground shadow-sm"
          : "hover:bg-background/60 hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("TabsContent needs Tabs parent");
  if (ctx.value !== value) return null;
  return (
    <div role="tabpanel" className={cn("mt-3", className)}>
      {children}
    </div>
  );
}
